import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Briefcase,
  BriefcaseBusiness,
  CalendarClock,
  CheckCircle2,
  FolderOpen,
  RefreshCw,
  ShieldAlert,
} from 'lucide-react';
import api from '../../apiPm';
import {
  getHealthTone,
  getSafeApiErrorMessage,
  getStageTone,
  normalizeStageLabel,
  withTimeout,
} from '../PmShared/projectUtils';
import PmCalendarPanel from '../PmCalendar/PmCalendarPanel';
import './PmDashboard.css';

const EMPTY_SUMMARY = {
  summary: {
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    blockedProjects: 0,
    overdueProjects: 0,
    totalOpenTasks: 0,
    peopleInvolved: 0,
    workHoursThisWeek: 0,
  },
  stageOverview: [],
  healthOverview: [],
  portfolioProgress: {
    averagePlannedProgress: 0,
    averageActualProgress: 0,
    averageVariance: 0,
    completedTasks: 0,
    totalTasks: 0,
  },
  attentionNeeded: [],
  upcomingDeadlines: [],
  recentActivity: [],
};

const ISSUE_TONE = {
  Blocked: 'danger',
  Overdue: 'danger',
  'Behind plan': 'warning',
  'No recent update': 'secondary',
};

const DEADLINE_STATUS_TONE = {
  Overdue: 'danger',
  'Due Today': 'warning',
  'Due This Week': 'blue',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ATTENTION_LIMIT = 5;
const ACTIVITY_LIMIT = 8;

const issueRank = (issue) => ({
  Blocked: 1,
  Overdue: 2,
  'Behind plan': 3,
  'No recent update': 4,
}[issue] || 9);

const getVarianceInsight = (variance) => {
  if (variance < -5) return { text: 'Actual progress is behind planned schedule.', tone: 'danger' };
  if (variance > 5) return { text: 'Actual progress is ahead of plan.', tone: 'success' };
  return { text: 'Actual progress is on track.', tone: 'success' };
};

const humanizeActivityDescription = (activity) => {
  const description = activity?.description || activity?.action || 'Activity updated';
  const actor = activity?.actorName && !/^user #?[0-9]+$/i.test(activity.actorName) ? activity.actorName : 'System';
  return description
    .replace(/^User #?[0-9]+/i, actor)
    .replace(/User #?[0-9]+/gi, actor);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${formatDate(value)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Lets a row/card with role="button" respond to Enter/Space the same way a
// native <button> would — required wherever we make a non-button element
// (table row, distribution row, KPI card) clickable for keyboard users.
const handleKeyboardClick = (event, callback) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    callback();
  }
};

const KpiCard = ({ icon: Icon, tone, value, label, isLoading, isUnavailable, onClick, title }) => (
  <div className="col-6 col-md-3">
    <div
      className={`card h-100 border-0 shadow-sm pm-dash-kpi-card pm-dash-kpi-${tone}${onClick ? ' pm-dash-clickable' : ''}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => handleKeyboardClick(e, onClick) : undefined}
      title={onClick ? title : undefined}
    >
      <div className="card-body d-flex align-items-center gap-3 py-3">
        <div className="pm-dash-kpi-icon">
          <Icon size={20} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="text-muted text-truncate pm-dash-kpi-label">{label}</div>
          <div className="fw-bold mt-1 pm-dash-kpi-value">
            {isLoading ? <span className="placeholder col-5" /> : isUnavailable ? '-' : value}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SectionCard = ({ title, subtitle, headerExtra, children, className = '' }) => (
  <div className={`card h-100 ${className}`}>
    {(title || subtitle || headerExtra) && (
      <div className="card-header pm-dash-section-header">
        <div>
          {title && <h3 className="card-title mb-0">{title}</h3>}
          {subtitle && <div className="text-muted pm-dash-section-subtitle">{subtitle}</div>}
        </div>
        {headerExtra && <div className="pm-dash-section-header-extra">{headerExtra}</div>}
      </div>
    )}
    <div className="card-body">{children}</div>
  </div>
);

const EmptyState = ({ icon: Icon = CheckCircle2, message }) => (
  <div className="pm-dash-empty-state text-muted">
    <Icon size={28} className="mb-2 opacity-25" />
    <div>{message}</div>
  </div>
);

const DistributionList = ({ rows, keyField, toneFn, emptyMessage, onRowClick, rowTitle }) => {
  const total = rows.reduce((sum, row) => sum + (row.count || 0), 0);
  if (total === 0) {
    return <EmptyState message={emptyMessage} />;
  }
  return (
    <div className="pm-dash-distribution">
      {rows.map((row) => {
        const label = row[keyField];
        const pct = total > 0 ? Math.round(((row.count || 0) / total) * 100) : 0;
        const rowClick = onRowClick ? () => onRowClick(row) : undefined;
        return (
          <div
            className={`pm-dash-distribution-row${rowClick ? ' pm-dash-clickable' : ''}`}
            key={label}
            role={rowClick ? 'button' : undefined}
            tabIndex={rowClick ? 0 : undefined}
            onClick={rowClick}
            onKeyDown={rowClick ? (e) => handleKeyboardClick(e, rowClick) : undefined}
            title={rowClick ? rowTitle : undefined}
          >
            <div className="pm-dash-distribution-label">
              <span className={`badge bg-${toneFn(label)}-lt`}>{label}</span>
            </div>
            <div className="progress pm-dash-distribution-bar">
              <div className={`progress-bar bg-${toneFn(label)}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="pm-dash-distribution-count">{row.count || 0}</div>
          </div>
        );
      })}
    </div>
  );
};

const PmDashboard = () => {
  const navigate = useNavigate();
  // `stageMacro` is an optional 'active' | 'completed' | 'blocked' keyword
  // — not a raw PROJECT_STAGES value — that PmProjects reads back out of
  // `?stage=` to pre-apply its Stage filter, so the KPI card's count and
  // the filtered list agree ('active' = everything PmDashboard's own
  // activeProjects counts as active, i.e. not Completed and not Blocked).
  const openProjects = (stageMacro) => navigate(stageMacro ? `/pm/projects?stage=${stageMacro}` : '/pm/projects');
  // Falls back to the plain project list whenever a row has no projectId —
  // covers Attention Needed items, deadlines, activity, and sessions alike.
  const openProjectDetail = (projectId) => {
    if (projectId) navigate(`/pm/projects/${projectId}`);
    else navigate('/pm/projects');
  };

  const [data, setData] = useState(EMPTY_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const response = await withTimeout(api.get('/v1/pm/dashboard/summary'));
      const payload = response.data || {};
      setData({
        summary: { ...EMPTY_SUMMARY.summary, ...(payload.summary || {}) },
        stageOverview: Array.isArray(payload.stageOverview) ? payload.stageOverview : [],
        healthOverview: Array.isArray(payload.healthOverview) ? payload.healthOverview : [],
        portfolioProgress: { ...EMPTY_SUMMARY.portfolioProgress, ...(payload.portfolioProgress || {}) },
        attentionNeeded: Array.isArray(payload.attentionNeeded) ? payload.attentionNeeded : [],
        upcomingDeadlines: Array.isArray(payload.upcomingDeadlines) ? payload.upcomingDeadlines : [],
        recentActivity: Array.isArray(payload.recentActivity) ? payload.recentActivity : [],
      });
    } catch (error) {
      setErrorMessage(getSafeApiErrorMessage(error, 'Unable to load the Project Management dashboard at the moment.'));
      setData(EMPTY_SUMMARY);
    } finally {
      setIsLoading(false);
    }
  };

  const isDataUnavailable = Boolean(errorMessage);

  const stageRows = useMemo(() => (
    data.stageOverview.map((row) => ({ ...row, stage: normalizeStageLabel(row.stage) }))
  ), [data.stageOverview]);

  const { summary, portfolioProgress, healthOverview, attentionNeeded, upcomingDeadlines, recentActivity } = data;

  const completedTaskRatio = portfolioProgress.totalTasks > 0
    ? Math.round((portfolioProgress.completedTasks / portfolioProgress.totalTasks) * 100)
    : 0;

  const varianceInsight = getVarianceInsight(portfolioProgress.averageVariance);

  const displayedAttention = useMemo(() => (
    attentionNeeded
      .slice()
      .sort((a, b) => issueRank(a.issue) - issueRank(b.issue))
      .slice(0, ATTENTION_LIMIT)
  ), [attentionNeeded]);

  const hiddenAttentionCount = Math.max(0, attentionNeeded.length - displayedAttention.length);

  const displayedActivity = useMemo(() => (
    recentActivity.slice(0, ACTIVITY_LIMIT)
  ), [recentActivity]);

  return (
    <div className="pm-dashboard-page">
      <div className="page-header d-print-none">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">Project Management</div>
            <h2 className="page-title">
              <Briefcase size={22} className="me-2 text-muted" />
              Project Management Dashboard
            </h2>
            <div className="text-muted mt-1">
              Monitor projects, delivery progress, task allocation, and upcoming work.
            </div>
          </div>
          <div className="col-auto ms-auto d-print-none">
            <div className="btn-list">
              <button
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={fetchSummary}
                disabled={isLoading}
              >
                <RefreshCw size={16} className={`me-2 ${isLoading ? 'pm-dash-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        {errorMessage && (
          <div className="alert alert-warning pm-dash-error-banner d-flex align-items-center" role="alert">
            <AlertTriangle size={18} className="me-3 flex-shrink-0" />
            <div className="flex-fill">
              <div className="fw-semibold">Dashboard data unavailable</div>
              <div className="pm-dash-helper-text">{errorMessage}</div>
            </div>
            <button className="btn btn-sm btn-outline-warning ms-3" onClick={fetchSummary}>Retry</button>
          </div>
        )}

        {/* Section 1: Summary Cards */}
        <div className="row g-3 mb-3">
          <KpiCard icon={FolderOpen} tone="primary" value={summary.totalProjects} label="Total Projects" isLoading={isLoading} isUnavailable={isDataUnavailable} onClick={openProjects} title="Open Projects" />
          <KpiCard icon={BriefcaseBusiness} tone="success" value={summary.activeProjects} label="Active Projects" isLoading={isLoading} isUnavailable={isDataUnavailable} onClick={() => openProjects('active')} title="Open Projects (Active)" />
          <KpiCard icon={CheckCircle2} tone="info" value={summary.completedProjects} label="Completed Projects" isLoading={isLoading} isUnavailable={isDataUnavailable} onClick={() => openProjects('completed')} title="Open Projects (Completed)" />
          <KpiCard icon={ShieldAlert} tone="danger" value={summary.blockedProjects} label="Blocked Projects" isLoading={isLoading} isUnavailable={isDataUnavailable} onClick={() => openProjects('blocked')} title="Open Projects (Blocked)" />
        </div>

        {/* Section 2: Stage + Health overview */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-lg-6">
            <SectionCard title="Project Stage Overview" subtitle="Distribution of projects by delivery stage">
              {isLoading ? (
                <div className="pm-dash-panel-loading"><span className="spinner-border spinner-border-sm" /></div>
              ) : (
                <DistributionList
                  rows={stageRows}
                  keyField="stage"
                  toneFn={getStageTone}
                  emptyMessage="No projects to show yet."
                  onRowClick={openProjects}
                  rowTitle="Open Projects"
                />
              )}
            </SectionCard>
          </div>
          <div className="col-12 col-lg-6">
            <SectionCard title="Project Health Overview" subtitle="Healthy vs at-risk vs blocked projects">
              {isLoading ? (
                <div className="pm-dash-panel-loading"><span className="spinner-border spinner-border-sm" /></div>
              ) : (
                <DistributionList
                  rows={healthOverview}
                  keyField="health"
                  toneFn={getHealthTone}
                  emptyMessage="No projects to show yet."
                  onRowClick={openProjects}
                  rowTitle="Open Projects"
                />
              )}
            </SectionCard>
          </div>
        </div>

        {/* Section 3: Portfolio Progress */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <SectionCard title="Portfolio Progress" subtitle="Planned vs actual delivery progress across all PM projects">
              {isLoading ? (
                <div className="pm-dash-panel-loading"><span className="spinner-border spinner-border-sm" /></div>
              ) : (
                <div className="row g-4">
                  <div className="col-12 col-md-6">
                    <div className="pm-dash-progress-metric">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Planned Progress (avg)</span>
                        <span className="fw-bold">{portfolioProgress.averagePlannedProgress}%</span>
                      </div>
                      <div className="progress pm-dash-progress-bar">
                        <div className="progress-bar bg-secondary" style={{ width: `${portfolioProgress.averagePlannedProgress}%` }} />
                      </div>
                    </div>
                    <div className="pm-dash-progress-metric mt-3">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Actual Progress (avg)</span>
                        <span className="fw-bold">{portfolioProgress.averageActualProgress}%</span>
                      </div>
                      <div className="progress pm-dash-progress-bar">
                        <div className="progress-bar bg-primary" style={{ width: `${portfolioProgress.averageActualProgress}%` }} />
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="pm-dash-stat-tile">
                      <div className="text-muted pm-dash-kpi-label">Average Variance</div>
                      <div className={`fw-bold pm-dash-stat-value ${portfolioProgress.averageVariance < 0 ? 'text-danger' : 'text-success'}`}>
                        {portfolioProgress.averageVariance > 0 ? '+' : ''}{portfolioProgress.averageVariance}%
                      </div>
                      <div className={`text-${varianceInsight.tone} pm-dash-helper-text`}>
                        {varianceInsight.text}
                      </div>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="pm-dash-stat-tile">
                      <div className="text-muted pm-dash-kpi-label">Completed Tasks Ratio</div>
                      <div className="fw-bold pm-dash-stat-value">{completedTaskRatio}%</div>
                      <div className="text-muted pm-dash-helper-text">
                        {portfolioProgress.completedTasks} / {portfolioProgress.totalTasks} tasks done
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Section 4: Attention Needed */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <SectionCard title="Attention Needed" subtitle="Projects with blockers, overdue work, or stalled progress">
              <div className="table-responsive">
                <table className="table table-vcenter pm-dash-table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>PIC</th>
                      <th>Stage</th>
                      <th>Progress</th>
                      <th>Deadline</th>
                      <th>Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr><td colSpan={6}><span className="placeholder col-12" /></td></tr>
                    ) : displayedAttention.length === 0 ? (
                      <tr>
                        <td colSpan={6}>
                          <EmptyState message="No projects need attention right now." />
                        </td>
                      </tr>
                    ) : (
                      displayedAttention.map((item) => (
                        <tr
                          key={item.projectId}
                          className="pm-dash-clickable-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => openProjectDetail(item.projectId)}
                          onKeyDown={(e) => handleKeyboardClick(e, () => openProjectDetail(item.projectId))}
                          title="Open project detail"
                        >
                          <td className="fw-semibold">{item.projectTitle || '-'}</td>
                          <td>{item.picName || '-'}</td>
                          <td><span className={`badge bg-${getStageTone(normalizeStageLabel(item.stage))}-lt`}>{normalizeStageLabel(item.stage) || '-'}</span></td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress pm-dash-inline-progress">
                                <div className="progress-bar bg-primary" style={{ width: `${item.progress || 0}%` }} />
                              </div>
                              <span className="pm-dash-helper-text">{item.progress || 0}%</span>
                            </div>
                          </td>
                          <td>{formatDate(item.deadline)}</td>
                          <td><span className={`badge bg-${ISSUE_TONE[item.issue] || 'secondary'}-lt`}>{item.issue}</span></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {hiddenAttentionCount > 0 && (
                <div className="pm-dash-helper-text text-muted mt-2">
                  +{hiddenAttentionCount} more project{hiddenAttentionCount === 1 ? '' : 's'} need attention — showing the {ATTENTION_LIMIT} most critical.
                </div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Section 5: Team Calendar */}
        <div className="row g-3 mb-3">
          <div className="col-12">
            <SectionCard className="pm-dash-calendar-section">
              <PmCalendarPanel
                embedded
                title="Team Calendar"
                subtitle="Shows when team members are already blocked by assigned and collaborative PM tasks."
              />
            </SectionCard>
          </div>
        </div>

        {/* Section 6: Upcoming Deadlines + Recent Activity */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-xl-6">
            <SectionCard title="Upcoming Deadlines" subtitle="Overdue, due today, and due this week">
              <div className="table-responsive">
                <table className="table table-vcenter pm-dash-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Project</th>
                      <th>Owner</th>
                      <th>Due Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr><td colSpan={5}><span className="placeholder col-12" /></td></tr>
                    ) : upcomingDeadlines.length === 0 ? (
                      <tr>
                        <td colSpan={5}>
                          <EmptyState icon={CalendarClock} message="No upcoming deadlines." />
                        </td>
                      </tr>
                    ) : (
                      upcomingDeadlines.map((item) => (
                        <tr
                          key={`${item.type}-${item.itemId}`}
                          className="pm-dash-clickable-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => openProjectDetail(item.projectId)}
                          onKeyDown={(e) => handleKeyboardClick(e, () => openProjectDetail(item.projectId))}
                          title={item.projectId ? 'Open project detail' : 'Open Projects'}
                        >
                          <td className="fw-semibold">{item.itemTitle || '-'}</td>
                          <td className="pm-dash-helper-text">{item.projectTitle || '-'}</td>
                          <td>{item.ownerName || '-'}</td>
                          <td>{formatDate(item.dueDate)}</td>
                          <td><span className={`badge bg-${DEADLINE_STATUS_TONE[item.status] || 'secondary'}-lt`}>{item.status}</span></td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>
          <div className="col-12 col-xl-6">
            <SectionCard title="Recent Activity" subtitle="Latest updates across PM projects">
              {isLoading ? (
                <div className="pm-dash-panel-loading"><span className="spinner-border spinner-border-sm" /></div>
              ) : displayedActivity.length === 0 ? (
                <EmptyState message="No recent activity yet." />
              ) : (
                <ul className="pm-dash-activity-list">
                  {displayedActivity.map((activity) => (
                    <li
                      key={activity.id}
                      className="pm-dash-activity-item pm-dash-clickable"
                      role="button"
                      tabIndex={0}
                      onClick={() => openProjectDetail(activity.projectId)}
                      onKeyDown={(e) => handleKeyboardClick(e, () => openProjectDetail(activity.projectId))}
                      title={activity.projectId ? 'Open project detail' : 'Open Projects'}
                    >
                      <div className="pm-dash-activity-dot" />
                      <div className="flex-fill">
                        <div className="pm-dash-activity-description">{humanizeActivityDescription(activity)}</div>
                        <div className="pm-dash-helper-text">{formatDateTime(activity.createdAt)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PmDashboard;
